import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import CustomGrid from 'common/components/CustomGrid'
import Table from 'common/components/Table'
import { formatDateTimeShort, getLocaleFromFullDate } from 'common/libs/fechas'
import { translateMask } from '../libs/DateFieldsHelpers'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useFetchMisSolicitudes from '../hooks/useFetchMisSolicitudes'
import { getChipSolicitud } from '../libs/SolicitudesHelpers'
import { setSolicitudSeleccionada } from '../solicitudesSlice'
import SolicitudCard from './SolicitudCard'

const MisSolicitudes = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { response, loading, error } = useFetchMisSolicitudes()
  const [estados, setEstados] = useState([])
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [solicitudes, setSolicitudes] = useState([])
  const [orignalResponse, setOriginalResponse] = useState([])

  useEffect(() => {
    const seen = new Set()

    setEstados(
      response
        ?.map((solicitud) => {
          return { value: solicitud.status, label: solicitud.d_status }
        })
        .filter((item) => {
          if (!item.value || seen.has(item.value)) return false
          seen.add(item.value)
          return true
        })
        .sort((a, b) => a.value - b.value),
    )
    setOriginalResponse(response)
    setSolicitudes(response)
  }, [response])

  const handleSelectSolicitud = (id) => {
    const solicitud = solicitudes.filter((s) => s.id === Number(id))[0]
    solicitud.fecha = null
    solicitud.fecha_validacion = null
    solicitud.estado = null

    dispatch(setSolicitudSeleccionada(solicitud))
    navigate(`/solicitud/`, { state: { solicitud } })
  }

  const camposListado = useMemo(() => {
    const seen = new Map()
    solicitudes?.forEach((s) => {
      s.campos_listado?.forEach((c) => {
        if (!seen.has(c.codigo_campo)) seen.set(c.codigo_campo, c)
      })
    })
    return [...seen.values()].map((c) => ({
      codigo: c.codigo_campo,
      etiqueta: c.etiqueta,
      tipo_dato: c.tipo_dato,
      mascara_formato: c.mascara_formato,
    }))
  }, [solicitudes])

  const [rowsPerPage, setRowsPerPage] = useState(12)

  const handleCardClick = (e) => {
    const solicitud = parseInt(e.currentTarget.dataset.solicitud)
    handleSelectSolicitud(solicitud)
  }

  const handleSelectedStatus = (event) => {
    setSelectedStatus(event.target.value)

    setSolicitudes(
      event.target.value
        ? orignalResponse.filter((solicitud) => solicitud.status === event.target.value)
        : orignalResponse,
    )
  }

  return (
    <>
      <Table
        sx={{ display: { xs: 'none', lg: 'block' } }}
        onRowClick={(event) => handleSelectSolicitud(event.row.id)}
        dataSource={solicitudes ?? []}
        page={1}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChanged={setRowsPerPage}
        count={solicitudes?.length}
        showInternalSearch={true}
        loading={loading}
        outSideFilters={[
          <FormControl
            key="estados-filter"
            variant="standard"
            sx={{ m: 1, minWidth: '200px', '& .MuiInputBase-root': { minHeight: 'unset' } }}
          >
            <InputLabel id="estados-filter">Estado</InputLabel>
            <Select
              labelId="estados-filter"
              id="estados-filter"
              value={selectedStatus}
              onChange={handleSelectedStatus}
              label="Estado"
            >
              <MenuItem key="no_estado" value={null}>
                Todos
              </MenuItem>
              {estados?.map((estado) => (
                <MenuItem key={estado.value} value={estado.value}>
                  {estado.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>,
        ]}
        fields={[
          { title: 'ID', field: 'codigo_solicitud', flex: 0, sortable: true },
          { title: 'Tipo', field: 'd_tipo', flex: 1, sortable: true },
          {
            title: 'Fecha',
            field: 'fecha',
            customGetter: (item) => formatDateTimeShort(item?.fecha_creacion),
          },
          ...camposListado.map(({ codigo, etiqueta, tipo_dato, mascara_formato }) => ({
            title: etiqueta,
            field: `campo_${codigo}`,
            flex: 1,
            customGetter: (item) => {
              const campo = item.campos_listado?.find((c) => c.codigo_campo === codigo)
              if (!campo) return ''
              const valor = campo.valor
              if (!valor) return ''
              if (['D', 'DH1', 'DH2'].includes(tipo_dato) && mascara_formato) {
                const { options } = translateMask(mascara_formato)
                return getLocaleFromFullDate(valor, true, 'es-Es', options)
              }
              return valor
            },
          })),
          {
            title: 'Validación',
            field: 'fecha_validacion',
            customGetter: (item) =>
              item?.row?.status_fecha ? getLocaleFromFullDate(item?.status_fecha) : '',
          },
          {
            field: 'estado',
            title: 'Estado',
            width: 200,
            filterable: false,
            sortable: false,
            customGetter: (item) => {
              return getChipSolicitud(item.status, item.d_status)
            },
          },
        ]}
      />
      <CustomGrid
        sx={{ display: { xs: 'block', lg: 'none' } }}
        dataSource={solicitudes ?? []}
        page={1}
        count={solicitudes?.length}
        loading={loading}
        pageLimit={rowsPerPage}
        showInternalSearch={true}
        CardLayout={SolicitudCard}
        gridProps={{ spacing: 2 }}
        gridItemsProps={{ size: { xs: 12, md: 6 } }}
        onClick={handleCardClick}
        outSideFilters={[
          <FormControl
            key="estados-filter"
            variant="standard"
            sx={{ m: 1, minWidth: '200px', '& .MuiInputBase-root': { minHeight: 'unset' } }}
          >
            <InputLabel id="estados-filter">Estado</InputLabel>
            <Select
              labelId="estados-filter"
              id="estados-filter"
              value={selectedStatus}
              onChange={handleSelectedStatus}
              label="Estado"
            >
              <MenuItem key="no_estado" value={null}>
                Todos
              </MenuItem>
              {estados?.map((estado) => (
                <MenuItem key={estado.value} value={estado.value}>
                  {estado.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>,
        ]}
      />
    </>
  )
}

export default MisSolicitudes
